<template>
	<div id="step-four-form" class="step-form">
		<form>
			<p class="form-text">Where will you be filming? (select all that apply):</p>

			<div class="form-row">
				<CheckboxInput
					inputId="cityBuilding"
					labelText="City-owned building"
					v-model="cityBuilding"
					customError="You must select at least one filming location type."
					:isRequired="!cityPark && !privateAddress && !otherOption"
				/> 
			</div>
			<div class="form-row">
				<CheckboxInput
					inputId="cityPark"
					labelText="Park"
					v-model="cityPark"
					customError="You must select at least one filming location type."
					:isRequired="!cityBuilding && !privateAddress && !otherOption"
				/>
			</div>
			<div class="form-row">
				<CheckboxInput
					inputId="privateAddress"
					labelText="Private address"
					v-model="privateAddress"
					customError="You must select at least one filming location type."
					:isRequired="!cityPark && !cityBuilding && !otherOption"
				/>
			</div>
			<div class="form-row">
				<CheckboxInput
					inputId="otherOption"
					labelText="Other"
					v-model="otherOption"
					customError="You must select at least one filming location type."
					:isRequired="false"
					:optionalFlag ="false"
				/>
			</div>

			<fieldset v-show="cityBuilding">
				<p class="form-subheading">City-owned building</p>
				<div class="form-row">
					<TypeaheadInput
						inputId="Facility_Building_Name"
						v-model="Facility_Building_Name"
						labelText="City facility"
						:isRequired="cityBuilding"
						:options="facilityList"
						:wrapperClasses="['col-md-10']"
					/>
				</div>
				<label class="form-label">Start date and time</label>
				<DateTimeInput
					inputId="Facility_Building_StartDate"
					v-model="Facility_Building_StartDate"
					:isRequired="cityBuilding"
					:srOnly="true"
					:defaultTime="{hour:7,minute:0}"
				/>
				<label class="form-label">End date and time</label>
				<DateTimeInput
					inputId="Facility_Building_EndDate"
					v-model="Facility_Building_EndDate"
					:isRequired="cityBuilding"
					:srOnly="true"
					:startDate="Facility_Building_StartDate"
					:defaultTime="{hour:23,minute:0}"
					:validation="[{type:'before',value: Facility_Building_StartDate, msg: 'Your end date can\'t be before your start date.'}]"
				/>
			</fieldset>

			<fieldset v-show="cityPark">
				<p class="form-subheading">Park</p>
				<div class="form-row">
					<TypeaheadInput
						inputId="Facility_Park_Name"
						v-model="Facility_Park_Name"
						labelText="Park name"
						:isRequired="cityPark"
						:options="parkList"
						:wrapperClasses="['col-md-10']"
					/>
				</div>
				<label class="form-label">Start date and time</label>
				<DateTimeInput
					inputId="Facility_Park_StartDate"
					v-model="Facility_Park_StartDate"
					:isRequired="cityPark"
					:srOnly="true"
					:defaultTime="{hour:7,minute:0}"
				/>
				<label class="form-label">End date and time</label>
				<DateTimeInput
					inputId="Facility_Park_EndDate"
					v-model="Facility_Park_EndDate"
					:startDate="Facility_Park_StartDate"
					:isRequired="cityPark"
					:srOnly="true"
					:defaultTime="{hour:23,minute:0}"
					:validation="[{type:'before',value: Facility_Park_StartDate, msg: 'Your end date can\'t be before your start date.'}]"
				/>
			</fieldset>

			<fieldset v-show="privateAddress">
				<p class="form-subheading">Private address</p>
				<div class="form-row">
					<TextInput
						inputId="Address1Number"
						v-model="Address1Number"
						labelText="Address number"
						:isRequired="privateAddress"
						:validation="addressNameList.includes(Address1Name) && !loading ? [{type:'match',value: address1List,msg:'This address number isn\'t valid for that street.'}] : []"
						:wrapperClasses="['col-md-4','my-3']"
					/>
					<TypeaheadInput
						inputId="Address1Name"
						v-model="Address1Name"
						labelText="Street name"
						:isRequired="privateAddress"
						:options="addressNameList"
						:wrapperClasses="['col-md-6']"
					/>
				</div>
				<div class="form-row">
					<CheckboxInput
						inputId="privateAddress2"
						labelText="Add a second private address where filming will take place"
						v-model="privateAddress2"
						:isRequired="false"
					/>
				</div>
			</fieldset>
			<fieldset v-show="privateAddress2">
				<div class="form-row">
					<TextInput
						inputId="Address2Number"
						v-model="Address2Number"
						labelText="Address number"
						:isRequired="privateAddress2"
						:validation="addressNameList.includes(Address2Name) && !loading
						? [{type:'match',value:address2List,msg:'This address number isn\'t valid for that street.'}] : []"
						:wrapperClasses="['col-md-4','my-3']"
					/>

					<TypeaheadInput
						inputId="Address2Name"
						v-model="Address2Name"
						labelText="Street name"
						:isRequired="privateAddress2"
						:options="addressNameList"
						:wrapperClasses="['col-md-6']"
					/>
				</div>
			</fieldset>
		</form>

		<div
			class="alert alert-danger mt-3"
			v-if="_errorsExist && submitted"
		>Please make sure you've filled all the fields out correctly.</div>
		<div class="d-flex justify-content-begin align-items-center">
			
			<button
                id="previousStep"
                class="mt-3 mr-3 btn btn-secondary"
                @click="previousStep"
            >Previous</button>
			<button
				id="nextStep"
				:class="['mt-3 btn btn-primary', !oneTypePicked || loading ? 'disabled' : null]"
				@click="nextStep"
				@mouseover.self="nextHover = true"
				@mouseout.self="nextHover = false"
			>Next</button>

			<div
				class="form-text text-danger ml-3"
				v-show="nextHover && !oneTypePicked"
			>Please select at least one location type before proceeding.</div>
			<div class="spinner-container" v-if="loading">
				<div class="spinner"></div>
			</div>
		</div>
	</div>
</template>

<script>
import CheckboxInput from "@/components/inputs/CheckboxInput.vue";
import TypeaheadInput from "@/components/inputs/TypeaheadInput.vue";
import TextInput from "@/components/inputs/TextInput.vue";
import DateTimeInput from "@/components/inputs/DateTimeInput.vue";

export default {
	//### BKUP ###
	//name: "StepFourForm",
	name: "StepThreeForm",
	components: {
		CheckboxInput,
		TypeaheadInput,
		TextInput,
		DateTimeInput
	},
	data: function() {
		return {
			type: "filming",
			locationType: "City facility",
			//### BKUP ###
			//step: 4,
			step: 3,
			submitted: false,
			nextHover: false
		};
	},
	computed: {
		oneTypePicked() {
			//return this.cityBuilding || this.cityPark || this.privateAddress
			return this.cityBuilding || this.cityPark || this.privateAddress || this.otherOption;
		},
		addressNameList() {
			return this.$store.getters.getRoadList;
		},
		address1List() {
			return this.$store.getters.Address1List.addressList.map(
				item => item.number
			);
		},
		address2List() {
			return this.$store.getters.Address2List.addressList.map(
				item => item.number
			);
		},
		cityBuilding: {
			get() {
				return this.$store.state.locationTypes.cityBuilding;
			},
			set(value) {
				if (!value) {
					// Clear the associated fields on false
					this.Facility_Building_Name = "";
					this.Facility_Building_StartDate = "";
					this.Facility_Building_EndDate = "";
				}

				this.$store.dispatch("UPDATE_LOCATION_TYPES", {
					field: "cityBuilding",
					data: value
				});
			}
		},
		cityPark: {
			get() {
				return this.$store.state.locationTypes.cityPark;
			},
			set(value) {
				if (!value) {
					// Clear the associated fields on false
					this.Facility_Park_Name = "";
					this.Facility_Park_StartDate = "";
					this.Facility_Park_EndDate = "";
				}

				this.$store.dispatch("UPDATE_LOCATION_TYPES", {
					field: "cityPark",
					data: value
				});
			}
		},
		privateAddress: {
			get() {
				return this.$store.state.locationTypes.privateAddress;
			},
			set(value) {
				if (!value) {
					// Set the other private address field to false
					this.privateAddress2 = "";

					// Clear the associated fields on false
					this.Address1Name = "";
					this.Address1Number = "";
				}

				this.$store.dispatch("UPDATE_LOCATION_TYPES", {
					field: "privateAddress",
					data: value
				});
			}
		},
		privateAddress2: {
			get() {
				return this.$store.state.locationTypes.privateAddress2;
			},
			set(value) {
				if (!value) {
					// Clear the associated fields on false
					this.Address2Name = "";
					this.Address2Number = "";
				}

				this.$store.dispatch("UPDATE_LOCATION_TYPES", {
					field: "privateAddress2",
					data: value
				});
			}
		},
		otherOption: {
			get() {
				return this.$store.state.locationTypes.otherOption;
			},
			set(value) {
				this.$store.dispatch("UPDATE_LOCATION_TYPES", {
					field: "otherOption",
					data: value
				});
			}
		},
		Facility_Building_Name: {
			get() {
				return this.$store.state.formData.Facility_Building_Name;
			},
			set(value) {
				this.$store.dispatch("UPDATE_FORM_FIELD", {
					field: "Facility_Building_Name",
					data: value
				});

				this.$store.dispatch("UPDATE_FORM_FIELD", {
					field: "Facility_Building_AssetId",
					data: this.$store.getters.facilityId(value)
				});
			}
		},
		Facility_Building_StartDate: {
			get() {
				return this.$store.state.formData.Facility_Building_StartDate;
			},
			set(value) {
				this.$store.dispatch("UPDATE_FORM_FIELD", {
					field: "Facility_Building_StartDate",
					data: value
				});
			}
		},
		Facility_Building_EndDate: {
			get() {
				return this.$store.state.formData.Facility_Building_EndDate;
			},
			set(value) {
				this.$store.dispatch("UPDATE_FORM_FIELD", {
					field: "Facility_Building_EndDate",
					data: value
				});
			}
		},
		Facility_Park_Name: {
			get() {
				return this.$store.state.formData.Facility_Park_Name;
			},
			set(value) {
				this.$store.dispatch("UPDATE_FORM_FIELD", {
					field: "Facility_Park_Name",
					data: value
				});

				this.$store.dispatch("UPDATE_FORM_FIELD", {
					field: "Facility_Park_AssetId",
					data: this.$store.getters.facilityId(value)
				});
			}
		},
		Facility_Park_StartDate: {
			get() {
				return this.$store.state.formData.Facility_Park_StartDate;
			},
			set(value) {
				this.$store.dispatch("UPDATE_FORM_FIELD", {
					field: "Facility_Park_StartDate",
					data: value
				});
			}
		},
		Facility_Park_EndDate: {
			get() {
				return this.$store.state.formData.Facility_Park_EndDate;
			},
			set(value) {
				this.$store.dispatch("UPDATE_FORM_FIELD", {
					field: "Facility_Park_EndDate",
					data: value
				});
			}
		},
		Address1Name: {
			get() {
				return this.$store.state.formData.SelectedFilmingLocationList[0]
					.AddressName;
			},
			set(value) {
				this.$store.dispatch("UPDATE_PRIVATE_ADDRESS_FIELD", {
					index: 0,
					field: "AddressName",
					data: value
				});
			}
		},
		Address1Number: {
			get() {
				return this.$store.state.formData.SelectedFilmingLocationList[0]
					.AddressNumber;
			},
			set(value) {
				this.$store.dispatch("UPDATE_PRIVATE_ADDRESS_FIELD", {
					index: 0,
					field: "AddressNumber",
					data: value
				});
			}
		},
		Address2Name: {
			get() {
				return this.$store.state.formData.SelectedFilmingLocationList[1]
					.AddressName;
			},
			set(value) {
				this.$store.dispatch("UPDATE_PRIVATE_ADDRESS_FIELD", {
					index: 1,
					field: "AddressName",
					data: value
				});
			}
		},
		Address2Number: {
			get() {
				return this.$store.state.formData.SelectedFilmingLocationList[1]
					.AddressNumber;
			},
			set(value) {
				this.$store.dispatch("UPDATE_PRIVATE_ADDRESS_FIELD", {
					index: 1,
					field: "AddressNumber",
					data: value
				});
			}
		},
		facilityList() {
			return this.$store.getters.getFacilityList;
		},
		parkList() {
			return this.$store.getters.getParkList;
		},
		loading() {
			return this.$store.getters.isLoading;
		}
	},
	methods: {
		nextStep() {
			this.submitted = true;

			if (!this._errorsExist()) {
				this.$store.dispatch("COMPLETE_STEP", {
					step: this.step,
					complete: true
				});
			}
		},
		previousStep() { 
                this.$store.dispatch('SET_STEP', this.step - 1);        
        },
	}
};
</script>

<style lang="scss" scoped>
.spinner {
	margin-right: 30px;
	margin-top: 15px;

	&::before {
		height: 30px;
		width: 30px;
	}
}
</style>
